import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import {
  RegPlate, StatusPill, Skeleton, EmptyState, Badge, Icon,
  ListControls, useListPrefs, type SortOption,
} from '../components/ui';
import { api, type Dashboard, type Defect } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

type Filter = 'all' | 'safety' | 'repair';
type Sort = 'newest' | 'oldest' | 'severity' | 'vehicle';

const SORTS: SortOption<Sort>[] = [
  { key: 'newest', label: 'Newest first', hint: 'Most recently raised' },
  { key: 'oldest', label: 'Oldest first', hint: 'Longest outstanding' },
  { key: 'severity', label: 'Safety items first' },
  { key: 'vehicle', label: 'Vehicle A-Z' },
];

const RANK: Record<string, number> = { safety: 0, repair: 1, monitor: 2 };

export default function Defects() {
  const [prefs, setPrefs, resetPrefs] = useListPrefs('defects', {
    filter: 'all' as Filter,
    sort: 'newest' as Sort,
  });
  const { filter, sort } = prefs;
  const { data, loading, error, reload } = useLoad<Dashboard>(() => api.get('/dashboard'));

  const defects = useMemo(() => {
    const list = (data?.openDefects ?? []).filter((d) => filter === 'all' || d.severity === filter);
    const id = (d: Defect) => (d.reg_no ?? d.fleet_no ?? '').toUpperCase();

    const sorted = [...list];
    switch (sort) {
      case 'oldest':
        sorted.sort((a, b) => a.created_at.localeCompare(b.created_at));
        break;
      case 'severity':
        sorted.sort(
          (a, b) => (RANK[a.severity] ?? 9) - (RANK[b.severity] ?? 9) || b.created_at.localeCompare(a.created_at),
        );
        break;
      case 'vehicle':
        sorted.sort((a, b) => id(a).localeCompare(id(b)) || b.created_at.localeCompare(a.created_at));
        break;
      default:
        sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
    }
    return sorted;
  }, [data, filter, sort]);

  const safetyCount = data?.openDefects.filter((d) => d.severity === 'safety').length ?? 0;

  return (
    <Screen
      onRefresh={reload} title="Open defects" subtitle="Faults awaiting rectification" back="/">
      <div className="stack-4">
        {safetyCount > 0 ? (
          <div className="notice notice-danger">
            <Icon name="warning" size={20} />
            <div>
              <strong>
                {safetyCount} safety defect{safetyCount === 1 ? '' : 's'} outstanding
              </strong>
              <div className="small" style={{ marginTop: 2 }}>
                A vehicle with an outstanding safety item should not be used on the road.
              </div>
            </div>
          </div>
        ) : null}

        <ListControls<Filter, Sort>
          chips={[
            { key: 'all', label: 'All' },
            { key: 'safety', label: 'Safety' },
            { key: 'repair', label: 'Repair' },
          ]}
          chip={filter}
          onChip={(key) => setPrefs({ filter: key })}
          sortOptions={SORTS}
          sort={sort}
          onSort={(key) => setPrefs({ sort: key })}
          activeFilterCount={(filter !== 'all' ? 1 : 0) + (sort !== 'newest' ? 1 : 0)}
          onClearFilters={resetPrefs}
        />

        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && defects.length === 0 ? (
          <EmptyState
            title="Nothing outstanding"
            body="Every recorded fault has been rectified and signed off."
          />
        ) : null}

        {defects.length > 0 && (
          <div className="panel divide">
            {defects.map((defect) => (
              <Link
                key={defect.id}
                to={defect.inspection_id ? `/inspections/${defect.inspection_id}/run` : `/fleet/${defect.asset_id}`}
                className="listrow"
                style={{ alignItems: 'flex-start' }}
              >
                <div className="listrow-lead">
                  <RegPlate reg={defect.reg_no} fleetNo={defect.fleet_no} size="sm" />
                </div>
                <div className="grow">
                  <div className="listrow-title">{defect.description}</div>
                  <div className="listrow-sub wrap">
                    {defect.im_no ? <Badge>IM {defect.im_no}</Badge> : null}
                    <Badge tone={defect.severity}>
                      {defect.severity === 'safety' ? 'Safety' : defect.severity === 'repair' ? 'Repair' : 'Monitor'}
                    </Badge>
                    <span className="dot" />
                    {defect.customer_name ?? 'No customer'}
                    <span className="dot" />
                    {formatDate(defect.created_at.slice(0, 10))}
                  </div>
                </div>
                <StatusPill state="overdue" size="sm">
                  Open
                </StatusPill>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}
